import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Select } from '../../../../components/Select';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import Loading from '../../../../components/Loading';
import { useWarehouseProductGroup } from '../../../../hooks/useWarehouseProductGroup';

interface AddWarehouseProductGroup {
    group_name: string;
    group_code: string;
}

const AddWarehouseProductGroupPage = () => {
    const navigate = useNavigate();

    const [group, setGroup] = useState<AddWarehouseProductGroup>({
        group_name: '',
        group_code: '',
    });
    const { createGroup } = useWarehouseProductGroup();

    useEffect(() => {
        document.title = "Add Product Group";
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setGroup(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!group.group_name) {
            showErrorToast("Please fill in all fields before submit.");
            return;
        }

        try {
            const res = await createGroup(group);
            if (res.dbsuccess) {
                showLoadingToast(`Creating ${group.group_name} ...`, 100);
                setTimeout(() => {
                    navigate('/gp/inventories/groups');
                    showSuccessToast(`${group.group_name} added successfully`);
                }, 1000);
            } else {
                showErrorToast(`Failed creating group data`);
            }
        } catch (err) {
            showErrorToast(`Failed to add group: ${err}`);
        }
    };

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Add Product Group</Heading>
                <div className='mt-5 flex lg:ml-4 lg:mt-0'>
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className='ml-3' disabled={!group.group_name}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Product Group Information</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Grouping of products that share similar supplier.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Group Name</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Group Name"
                                        name="group_name"
                                        value={group.group_name}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Group Code</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Group Code"
                                        name="group_code"
                                        value={group.group_code}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>
            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={!group.group_name}>Save</Button>
            </div>
        </form>
    );
};

export default AddWarehouseProductGroupPage;
